/* eslint-disable max-len, object-curly-newline, react/jsx-one-expression-per-line, newline-per-chained-call, react/jsx-wrap-multilines, implicit-arrow-linebreak, function-paren-newline, operator-linebreak, react/jsx-curly-newline */
import React, { useContext, useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import AccessTime from '@mui/icons-material/AccessTime';
import PauseCircleOutline from '@mui/icons-material/PauseCircleOutline';
import AssignmentTurnedInOutlined from '@mui/icons-material/AssignmentTurnedInOutlined';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import VerifiedUserOutlined from '@mui/icons-material/VerifiedUserOutlined';
import ScheduleOutlined from '@mui/icons-material/ScheduleOutlined';
import Shuffle from '@mui/icons-material/Shuffle';
import Autorenew from '@mui/icons-material/Autorenew';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import Close from '@mui/icons-material/Close';
import NavigationLayout from '../../components/NavigationLayout';
import CustomTable from '../../components/table/CustomTable';
import DateRangePicker from '../../components/dateRangePicker';
import { NotificationContext } from '../../contexts/Notification';
import { UserContext } from '../../contexts/User';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';

/*
 * Chart Abstraction — admin monitoring. Live view of chart processing + QA,
 * backed by /chart-abstraction/admin/overview (polled) and /admin/productivity.
 * Complete / Deny call the shared QA endpoints. Auto-assign-to-QA happens
 * server-side (lightest queue) when a rep completes a chart.
 */

const pad = (n) => String(n).padStart(2, '0');
const fmt = (s) => `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
const initials = (name) => (name || '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();
const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const relTime = (ms) => {
  if (!ms) return '';
  const m = Math.max(0, Math.floor((Date.now() - ms) / 60000));
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const card = { background: '#fff', border: '1px solid var(--dark-6, #E0E1E6)', borderRadius: 12, boxShadow: '0 1px 2px rgba(14,30,64,0.04)' };
const cardHead = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px 8px' };
const cardTitle = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 600, color: 'var(--dark-1, #0E1E40)' };
const countPill = { fontSize: 12, fontWeight: 600, color: 'var(--dark-3, #546077)', background: 'var(--dark-7, #ECECF0)', borderRadius: 20, padding: '1px 9px' };
const row = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 20px', borderTop: '1px solid var(--dark-7, #ECECF0)' };
const idS = { fontSize: 14, fontWeight: 600, color: 'var(--dark-1, #0E1E40)' };
const subS = { fontSize: 12.5, color: 'var(--dark-3, #546077)', marginTop: 2 };
const pill = (bg, fg) => ({ display: 'inline-flex', alignItems: 'center', fontSize: 12.5, fontWeight: 600, borderRadius: 20, padding: '3px 11px', background: bg, color: fg, whiteSpace: 'nowrap' });
const avatar = (name) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: '50%', background: 'var(--blue-7, #D8E6FC)', color: 'var(--primary, #213D69)', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>{initials(name)}</span>
);

const KPI_DEFS = [
  { key: 'inProgress', label: 'In progress', icon: AccessTime, accent: '#213D69' },
  { key: 'pended', label: 'Pended', icon: PauseCircleOutline, accent: '#B3650F' },
  { key: 'awaiting', label: 'Awaiting QA', icon: AssignmentTurnedInOutlined, accent: '#2563eb' },
  { key: 'completedToday', label: 'Completed today', icon: CheckCircleOutline, accent: '#256D23' },
  { key: 'qaPassRate', label: 'QA pass rate', icon: VerifiedUserOutlined, accent: '#7c3aed', pct: true },
  { key: 'avgSeconds', label: 'Avg time / chart', icon: ScheduleOutlined, accent: '#546077', time: true },
];

const CA_TABLE_HEADERS = [
  { label: 'Member', colSpan: 2 }, { label: 'Role' }, { label: 'Charts' }, { label: 'Avg time' }, { label: 'Pended' },
];

function ChartAbstractionAdmin() {
  const { displayNotification } = useContext(NotificationContext);
  const { activeWorkspace } = useContext(UserContext);
  const workspaceId = activeWorkspace?.id;

  const [overview, setOverview] = useState(null);
  const [productivity, setProductivity] = useState({ members: [] });
  const [range, setRange] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nowTs, setNowTs] = useState(Date.now());
  const [denyTarget, setDenyTarget] = useState(null);
  const [denyReason, setDenyReason] = useState('');
  const [acting, setActing] = useState(false);
  const baseAtRef = useRef(Date.now());

  const fetchOverview = async () => {
    if (!workspaceId) return;
    const res = await sendRequest({ method: 'get', url: API.CHART_ABSTRACTION.adminOverview(workspaceId) });
    if (res.success) {
      setOverview(res.data);
      baseAtRef.current = Date.now();
    } else {
      displayNotification({ message: res.data?.error || 'Failed to load monitoring data', severity: 'error' });
    }
  };

  const fetchProductivity = async (r) => {
    if (!workspaceId) return;
    const from = r && r[0] ? toYmd(r[0]) : '';
    const to = r && r[1] ? toYmd(r[1]) : '';
    const res = await sendRequest({ method: 'get', url: API.CHART_ABSTRACTION.productivity(workspaceId, from, to) });
    if (res.success) setProductivity(res.data);
  };

  useEffect(() => {
    if (!workspaceId) return undefined;
    (async () => { setLoading(true); await fetchOverview(); setLoading(false); })();
    const poll = setInterval(fetchOverview, 15000);
    const tick = setInterval(() => setNowTs(Date.now()), 1000);
    return () => { clearInterval(poll); clearInterval(tick); };
  }, [workspaceId]);

  useEffect(() => { if (workspaceId) fetchProductivity(range); }, [workspaceId, range]);

  const handleComplete = async (item) => {
    if (acting) return;
    setActing(true);
    const res = await sendRequest({ method: 'post', url: API.CHART_ABSTRACTION.qaComplete(workspaceId, item.id) });
    setActing(false);
    if (res.success) {
      displayNotification({ message: `${item.recordId} signed off as QA complete`, severity: 'success' });
      fetchOverview();
    } else {
      displayNotification({ message: res.data?.error || 'Could not complete the chart', severity: 'error' });
    }
  };

  const confirmDeny = async () => {
    if (!denyTarget || !denyReason.trim() || acting) return;
    const item = denyTarget;
    setActing(true);
    const res = await sendRequest({ method: 'post', url: API.CHART_ABSTRACTION.qaDeny(workspaceId, item.id), data: { reason: denyReason.trim() } });
    setActing(false);
    if (res.success) {
      displayNotification({ message: `${item.recordId} sent back to ${item.repName} to review again`, severity: 'info' });
      setDenyTarget(null); setDenyReason('');
      fetchOverview();
    } else {
      displayNotification({ message: res.data?.error || 'Could not deny the chart', severity: 'error' });
    }
  };

  const exportCsv = () => {
    const rows = [['Member', 'Role', 'Charts', 'Avg time', 'Pended'],
      ...(productivity.members || []).map((m) => [m.name, m.role === 'QA' ? 'QA' : 'Abstraction Rep', m.charts, m.avgSeconds == null ? '—' : fmt(m.avgSeconds), m.pended == null ? '—' : m.pended])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = 'chart-abstraction-productivity.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const kpis = overview?.kpis || {};
  const kpiValue = (k) => {
    const v = kpis[k.key];
    if (k.pct) return v == null ? '—' : `${v}%`;
    if (k.time) return v == null ? '—' : fmt(v);
    return String(v == null ? 0 : v);
  };

  const working = overview?.currentlyWorking || [];
  const awaiting = overview?.awaiting || [];
  const denied = overview?.denied || [];
  const qaCompleted = overview?.qaCompleted || [];
  const workloads = overview?.workloads || [];
  const liveOffset = Math.floor((nowTs - baseAtRef.current) / 1000);

  const items = (productivity.members || []).map((m) => ({
    member: <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>{avatar(m.name)}<span style={{ fontSize: 14, fontWeight: 600, color: 'var(--dark-1, #0E1E40)' }}>{m.name}</span></div>,
    role: <span style={pill(m.role === 'QA' ? '#F3E8FF' : '#EAF1FF', m.role === 'QA' ? '#7c3aed' : '#2563eb')}>{m.role === 'QA' ? 'QA' : 'Abstraction Rep'}</span>,
    charts: <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--dark-1, #0E1E40)' }}>{m.charts}</span>,
    avg: <span style={{ fontSize: 14, color: 'var(--dark-2, #293855)' }}>{m.avgSeconds == null ? '—' : fmt(m.avgSeconds)}</span>,
    pended: <span style={{ fontSize: 14, color: 'var(--dark-2, #293855)' }}>{m.pended == null ? '—' : m.pended}</span>,
  }));

  return (
    <NavigationLayout title="Chart Abstraction" subtitle="Monitor chart processing and QA across your team">
      {loading && !overview ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}><CircularProgress size={30} /></div>
      ) : (
        <div style={{ marginTop: 24, fontFamily: 'Inter, sans-serif', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* KPI row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
            {KPI_DEFS.map((k) => {
              const Icon = k.icon;
              return (
                <div key={k.key} style={{ ...card, padding: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 8, background: `${k.accent}1a`, color: k.accent }}><Icon style={{ fontSize: 17 }} /></span>
                    <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--dark-3, #546077)' }}>{k.label}</span>
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--dark-1, #0E1E40)', lineHeight: 1 }}>{kpiValue(k)}</div>
                </div>
              );
            })}
          </div>

          {/* Auto-assign strip */}
          <div style={{ ...card, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, background: 'var(--blue-7, #D8E6FC)', color: 'var(--primary, #213D69)' }}><Shuffle style={{ fontSize: 17 }} /></span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--dark-1, #0E1E40)' }}>Auto-assign to QA</div>
                <div style={{ fontSize: 12.5, color: 'var(--dark-3, #546077)' }}>Completed charts route automatically to the reviewer with the lightest queue.</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--dark-3, #546077)', marginRight: 4 }}>Workload</span>
              {workloads.length === 0 ? <span style={{ fontSize: 12.5, color: 'var(--dark-4, #7D8698)' }}>No QA reviewers yet</span>
                : workloads.map((q) => <span key={q.memberId} style={pill('#EAF1FF', 'var(--primary, #213D69)')}>{`${q.name} · ${q.count}`}</span>)}
            </div>
          </div>

          {/* Currently working + Awaiting QA */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
            <div style={{ ...card, flex: 1, minWidth: 340 }}>
              <div style={cardHead}>
                <span style={cardTitle}><span style={{ width: 9, height: 9, borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} />Currently working</span>
                <span style={countPill}>{working.length}</span>
              </div>
              <div style={{ paddingBottom: 8 }}>
                {working.length === 0 ? <div style={{ padding: '24px 20px', textAlign: 'center', color: 'var(--dark-4, #7D8698)', fontSize: 14 }}>No charts in progress.</div>
                  : working.map((r, i) => (
                    <div key={r.id} style={{ ...row, borderTop: i === 0 ? 'none' : row.borderTop }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                        {avatar(r.repName)}
                        <span><span style={{ display: 'block', ...idS, fontWeight: 600 }}>{r.repName}</span><span style={{ display: 'block', fontSize: 12, color: 'var(--dark-3, #546077)' }}>{r.recordId}</span></span>
                      </div>
                      <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--dark-1, #0E1E40)', fontVariantNumeric: 'tabular-nums' }}>{fmt(r.elapsedSeconds + liveOffset)}</span>
                    </div>
                  ))}
              </div>
            </div>

            <div style={{ ...card, flex: 1, minWidth: 340 }}>
              <div style={cardHead}>
                <span style={cardTitle}><AssignmentTurnedInOutlined style={{ fontSize: 18, color: '#2563eb' }} />Awaiting QA</span>
                <span style={countPill}>{awaiting.length}</span>
              </div>
              <div style={{ paddingBottom: 8 }}>
                {awaiting.length === 0 ? <div style={{ padding: '24px 20px', textAlign: 'center', color: 'var(--dark-4, #7D8698)', fontSize: 14 }}>All caught up.</div>
                  : awaiting.map((c, i) => (
                    <div key={c.id} style={{ ...row, borderTop: i === 0 ? 'none' : row.borderTop }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={idS}>{c.recordId}</span>
                          {c.assigneeName && <span style={{ ...pill('#EAF1FF', 'var(--primary, #213D69)'), fontWeight: 500 }}>{c.assigneeName}</span>}
                        </div>
                        <div style={subS}>{`Abstracted by ${c.repName} · ${fmt(c.onChartSeconds)} on chart · completed ${relTime(c.completedAtMs)}`}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                        <Button size="small" variant="contained" disabled={acting} onClick={() => handleComplete(c)} sx={{ textTransform: 'none', backgroundColor: 'var(--green-3, #53A551)', '&:hover': { backgroundColor: 'var(--green-2, #378A35)' } }}>Complete</Button>
                        <Button size="small" variant="outlined" disabled={acting} onClick={() => { setDenyTarget(c); setDenyReason(''); }} sx={{ textTransform: 'none', color: 'var(--red-2, #CC4525)', borderColor: '#e6c3ba' }}>Deny</Button>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* Denied + QA completed */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
            <div style={{ ...card, flex: 1, minWidth: 340 }}>
              <div style={cardHead}>
                <span style={cardTitle}><Autorenew style={{ fontSize: 18, color: 'var(--red-2, #CC4525)' }} />Denied by QA — review again</span>
                <span style={countPill}>{denied.length}</span>
              </div>
              <div style={{ paddingBottom: 8 }}>
                {denied.length === 0 ? <div style={{ padding: '10px 20px 16px', color: 'var(--dark-3, #546077)', fontSize: 14 }}>No charts sent back. Denied charts return to the abstraction rep&apos;s queue for rework.</div>
                  : denied.map((c, i) => (
                    <div key={c.id} style={{ ...row, borderTop: i === 0 ? 'none' : row.borderTop }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={idS}>{c.recordId}</div>
                        <div style={{ ...subS, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{`Back to ${c.repName} · ${c.reason}`}</div>
                      </div>
                      <span style={pill('#FCECE7', 'var(--red-2, #CC4525)')}>Denied</span>
                    </div>
                  ))}
              </div>
            </div>

            <div style={{ ...card, flex: 1, minWidth: 340 }}>
              <div style={cardHead}>
                <span style={cardTitle}><CheckCircleOutline style={{ fontSize: 18, color: 'var(--green-1, #256D23)' }} />QA completed</span>
                <span style={countPill}>{qaCompleted.length}</span>
              </div>
              <div style={{ paddingBottom: 8 }}>
                {qaCompleted.length === 0 ? <div style={{ padding: '10px 20px 16px', color: 'var(--dark-3, #546077)', fontSize: 14 }}>No charts signed off yet today.</div>
                  : qaCompleted.map((c, i) => (
                    <div key={`${c.id}-${i}`} style={{ ...row, borderTop: i === 0 ? 'none' : row.borderTop }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={idS}>{c.recordId}</div>
                        <div style={subS}>{`Abstracted by ${c.repName} · ${fmt(c.onChartSeconds)} on chart · Reviewed by ${c.reviewerName || '—'} · ${relTime(c.reviewedAtMs)}`}</div>
                      </div>
                      <span style={pill('#E7F6E6', 'var(--green-1, #256D23)')}>QA complete</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* Team productivity */}
          <div style={card}>
            <div style={{ ...cardHead, flexWrap: 'wrap', gap: 12, paddingBottom: 8 }}>
              <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--dark-1, #0E1E40)' }}>Team productivity</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <DateRangePicker value={range} onChange={setRange} placement="bottomEnd" />
                <Button size="small" variant="outlined" startIcon={<FileDownloadOutlined />} onClick={exportCsv} sx={{ textTransform: 'none', color: 'var(--dark-2, #293855)', borderColor: 'var(--dark-6, #E0E1E6)' }}>Export</Button>
              </div>
            </div>
            <div style={{ padding: '0 8px 8px' }}>
              <CustomTable headers={CA_TABLE_HEADERS} items={items} noItemText="No activity for this period." />
            </div>
          </div>
        </div>
      )}

      <Dialog open={!!denyTarget} onClose={() => setDenyTarget(null)} maxWidth="xs" fullWidth>
        <div style={{ padding: '20px 24px', fontFamily: 'Inter, sans-serif' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <h5 style={{ fontSize: 18, fontWeight: 600, color: 'var(--dark-1, #0E1E40)', margin: 0 }}>Deny this chart</h5>
            <IconButton size="small" onClick={() => setDenyTarget(null)}><Close style={{ fontSize: 20 }} /></IconButton>
          </div>
          {denyTarget && (
            <div style={{ display: 'flex', gap: 8, padding: '10px 12px', borderRadius: 8, marginBottom: 16, background: '#FDF1E3', border: '1px solid #f2d9b3' }}>
              <Autorenew style={{ fontSize: 18, color: '#B3650F', flexShrink: 0 }} />
              <p style={{ fontSize: 13.5, color: '#8a5a12', margin: 0 }}>
                {'Denying '}
                <strong>{denyTarget.recordId}</strong>
                {' will send it back to '}
                <strong>{denyTarget.repName}</strong>
                {' (abstraction rep) to review again.'}
              </p>
            </div>
          )}
          <TextField label="Reason for denial" multiline minRows={3} fullWidth value={denyReason} onChange={(e) => setDenyReason(e.target.value)} placeholder="e.g. Diagnosis codes need to be re-checked against the source document" />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
            <Button variant="outlined" onClick={() => setDenyTarget(null)} sx={{ textTransform: 'none', color: 'var(--dark-2, #293855)', borderColor: 'var(--dark-6, #E0E1E6)' }}>Cancel</Button>
            <Button variant="contained" disabled={!denyReason.trim() || acting} startIcon={<Autorenew />} onClick={confirmDeny} sx={{ textTransform: 'none', backgroundColor: 'var(--red-2, #CC4525)', '&:hover': { backgroundColor: '#a8391d' } }}>Deny &amp; send back</Button>
          </div>
        </div>
      </Dialog>
    </NavigationLayout>
  );
}

export default ChartAbstractionAdmin;
